import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, KeyRound, Pencil, Plus, Settings2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Panel } from "../admin/fields";
import { CopyButton } from "../admin/EmbedTab";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { SiteDialog } from "./SiteDialog";

const Row = ({ label, value, testid }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-ink/[0.04] px-4 py-3">
    <div className="min-w-0"><p className="field-label">{label}</p><p className="break-all font-mono text-sm" data-testid={testid}>{value}</p></div>
    <CopyButton text={value} testid={`${testid}-copy`} />
  </div>
);

const LoginDialog = ({ result, onClose }) => {
  const origin = window.location.origin;
  const mail = result.login.email_status;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="font-sans sm:max-w-xl" data-testid="login-result-dialog">
        <DialogHeader><DialogTitle>Login for {result.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-ink/60">Copy the password now. It is shown only once.</p>
        <div className="space-y-2">
          <Row label="Booking page" value={`${origin}/s/${result.slug}`} testid="login-site-link" />
          <Row label="Admin sign-in" value={`${origin}/admin/login`} testid="login-admin-link" />
          <Row label="Email" value={result.login.email} testid="login-email" />
          <Row label="Temporary password" value={result.login.password} testid="login-password" />
        </div>
        <p className={`text-sm ${mail.startsWith("failed") ? "text-red-600" : "text-ink/60"}`} data-testid="login-email-status">
          {mail === "sent" ? `Login emailed to ${result.login.email}.` : mail === "not sent" ? "Not emailed. Send these details yourself." : `Email ${mail}`}
        </p>
      </DialogContent>
    </Dialog>
  );
};

const DAY = 86_400_000;

const LastLogin = ({ s }) => {
  if (!s.admin_email) return null;
  if (!s.last_login_at) return <p className="font-medium text-gold" data-testid={`site-last-login-${s.slug}`}>Never signed in</p>;
  const days = Math.floor((Date.now() - new Date(s.last_login_at).getTime()) / DAY);
  const when = days < 1 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;
  return (
    <p className={days >= 30 ? "font-medium text-red-600" : ""} data-testid={`site-last-login-${s.slug}`}>
      Last sign-in: {when} · {s.login_count} {s.login_count === 1 ? "sign-in" : "sign-ins"}{days >= 30 ? " · inactive" : ""}
    </p>
  );
};

const OwnerToggle = ({ s }) => {
  const qc = useQueryClient();
  const set = async (verified) => {
    try {
      await api.put(`/platform/sites/${s.slug}/verified-owner`, { verified });
      qc.invalidateQueries({ queryKey: ["platform-sites"] });
      toast.success(verified ? `${s.name} marked as verified hotel owner` : "Verified owner removed");
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <label className="mt-1 flex items-center gap-2 text-ink/70" title="Other sites listing these hotels use this site's photos and descriptions wherever they haven't added their own">
      <input type="checkbox" className="h-4 w-4 accent-brand" checked={s.verified_owner} onChange={(e) => set(e.target.checked)} data-testid={`site-verified-owner-${s.slug}`} />
      Verified hotel owner (shares content)
    </label>
  );
};

const SiteRow = ({ s, onEdit, onLogin }) => (
  <div className="grid gap-3 py-4 lg:grid-cols-[1.3fr_1fr_auto] lg:items-center" data-testid={`site-row-${s.slug}`}>
    <div className="min-w-0">
      <p className="font-medium">{s.name} {s.status !== "active" && <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">Disabled</span>}</p>
      <a href={`/s/${s.slug}`} target="_blank" rel="noreferrer" className="font-mono text-xs text-brand underline" data-testid={`site-link-${s.slug}`}>{window.location.origin}/s/{s.slug}</a>
    </div>
    <div className="text-xs text-ink/60">
      <p>{s.provider === "eres" ? s.eres_db_name || "eRes" : "Demo data"} · {s.hotel_codes.join(", ")}</p>
      <p>Admin: {s.admin_email || "none yet"}</p>
      <LastLogin s={s} />
      <OwnerToggle s={s} />
      <p data-testid={`site-domain-${s.slug}`}>Own address: {s.domain_option ? `${{ forward: "forwarded", embed: "embedded", platform: "platform link" }[s.domain_option]}${s.own_domain ? ` · ${s.own_domain}` : ""}` : "not chosen"}</p>
    </div>
    <div className="flex flex-wrap gap-2">
      <Link to={`/admin?site=${s.slug}`} className="btn-outline !py-2" data-testid={`site-manage-${s.slug}`}><Settings2 className="h-4 w-4" />Customise</Link>
      <button className="btn-ghost" onClick={() => onEdit(s)} title="Edit" data-testid={`site-edit-${s.slug}`}><Pencil className="h-4 w-4" /></button>
      <button className="btn-ghost" onClick={() => onLogin(s)} title="New password" data-testid={`site-login-${s.slug}`}><KeyRound className="h-4 w-4" /></button>
      <a href={`/s/${s.slug}`} target="_blank" rel="noreferrer" className="btn-ghost" title="Open" data-testid={`site-open-${s.slug}`}><ExternalLink className="h-4 w-4" /></a>
    </div>
  </div>
);

export const SitesPanel = () => {
  const { data } = useQuery({ queryKey: ["platform-sites"], queryFn: () => api.get("/platform/sites").then((r) => r.data) });
  const [edit, setEdit] = useState(null);
  const [result, setResult] = useState(null);
  const newLogin = async (s) => {
    const email = window.prompt(`Hotel admin email for ${s.name}. A new password will replace the old one.`, s.admin_email || "");
    if (email === null) return;
    const send = window.confirm("Email the new login to this address?");
    try {
      const { data: login } = await api.post(`/platform/sites/${s.slug}/login`, { email, send_email: send });
      setResult({ name: s.name, slug: s.slug, login });
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <Panel title="Hotel booking sites" hint="Each site has its own link, hotel admin login, branding, payments and email settings."
      action={<button className="btn-primary !py-2" onClick={() => setEdit({})} data-testid="site-create"><Plus className="h-4 w-4" />New site</button>}>
      <div className="divide-y divide-ink/10" data-testid="sites-list">
        {(data || []).map((s) => <SiteRow key={s.slug} s={s} onEdit={setEdit} onLogin={newLogin} />)}
      </div>
      {edit && <SiteDialog site={edit} onClose={() => setEdit(null)}
        onCreated={(r) => setResult({ name: r.site.name, slug: r.site.slug, login: r.login })} />}
      {result && <LoginDialog result={result} onClose={() => setResult(null)} />}
    </Panel>
  );
};
